import React from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from 'theme/Maintheme';
import { captureElementAsBlob } from './chartCapture';
import { _getMarginalAbatementCost } from 'middlewares/DashboardApi/projectMetrics';
import { generateData } from 'pages/Dashboards/ProjectRegister/data/metricsUtils';
import { ExportPackConfig } from 'object-models/ExportPackModel';
import { ProjectRegisterMetrics } from 'constants/constant';
import { MIBScopeData } from 'pages/Dashboards/ProjectRegister/data/MIBScopeData';
import MarginalAbatementCostCurve from 'pages/Dashboards/ProjectRegister/MarginalAbatementCostCurve';
import TimelineAndFinancialMetrics from 'pages/Dashboards/ProjectRegister/TimelineAndFinancialMetrics';
import MeasureImpactsbyScope from 'pages/Dashboards/ProjectRegister/MeasureImpactsbyScope';
import InteractiveGlidepath from 'pages/Dashboards/ProjectRegister/InteractiveGlidepath';

type ChartType = keyof ExportPackConfig['charts'];

export interface ChartExportResult {
  filename: string;
  blob: Blob;
}

export interface ChartExportError {
  chartType: ChartType;
  scenario: string;
  reason: string;
}

export interface ChartExportOutcome {
  results: ChartExportResult[];
  errors: ChartExportError[];
}

export interface ChartExportParams {
  config: ExportPackConfig;
  scenarios: string[];
  locationId: number;
  ghgScope: string[];
  startYear?: number | null;
  endYear?: number | null;
  currency: string;
  container: HTMLDivElement;
}

const CHART_LABELS: Record<ChartType, string> = {
  macCurve: 'MAC Curve',
  timelineFinancialMetrics: 'Timeline & Financial Metrics',
  measureImpactByScope: 'Measure Impact by Scope',
  glidepath: 'Glidepath',
};

const DASHBOARD_SECTIONS: Record<ChartType, string> = {
  macCurve: ProjectRegisterMetrics.MarginalAbatementCostCurve,
  timelineFinancialMetrics: ProjectRegisterMetrics.TimelineandFinancialMetrics,
  measureImpactByScope: ProjectRegisterMetrics.MeasureImpactsbyScope,
  glidepath: ProjectRegisterMetrics.InteractiveGlidepath,
};

const CHART_FILE_NAMES: Record<ChartType, string> = {
  macCurve: 'MAC_Curve',
  timelineFinancialMetrics: 'Timeline_Financial_Metrics',
  measureImpactByScope: 'Measure_Impact_by_Scope',
  glidepath: 'Glidepath',
};

function createChartElement(
  chartType: ChartType,
  apiResponse: any,
  currency: string,
  startYear?: number | null,
  endYear?: number | null
): React.ReactElement | null {
  switch (chartType) {
    case 'macCurve': {
      const data = apiResponse.maCgraphModelList ?? [];
      if (data.length === 0) return null;
      return React.createElement(MarginalAbatementCostCurve, {
        data,
        labelKey: 'reductionMeasureName' as const,
        currency,
      });
    }
    case 'timelineFinancialMetrics': {
      const timelineMetric = apiResponse?.timelineFinancialMetric;
      if (!Array.isArray(timelineMetric) || timelineMetric.length === 0) return null;
      // Without an explicit window there is no defensible year range to render.
      if (startYear == null || endYear == null) return null;
      const data = generateData(apiResponse, false, startYear, endYear);
      if (data.length === 0) return null;
      return React.createElement(TimelineAndFinancialMetrics, {
        data,
        width: 1100,
        height: 450,
      });
    }
    case 'measureImpactByScope': {
      const rawData = apiResponse.impactByScopeModelList ?? [];
      const data: MIBScopeData[] = rawData.map((item: any) => ({
        label: item.reductionMeasureName ?? 'Unknown',
        value: item.emissionReduction ?? 0,
        measures: item.measures ?? [],
        scopeByEmissions: item.scopeByEmissions ?? [],
      }));
      if (data.length === 0) return null;
      return React.createElement(MeasureImpactsbyScope, {
        data,
        currency,
        fitContainerWidth: true,
        isExportCapture: true,
      });
    }
    case 'glidepath': {
      const data = apiResponse.interactiveGlidepathMetric ?? [];
      if (data.length === 0) return null;
      return React.createElement(InteractiveGlidepath, {
        emissionsData: data,
      });
    }
    default:
      return null;
  }
}

async function waitForStableDOM(
  element: HTMLElement,
  idleMs = 400,
  maxMs = 3000
): Promise<void> {
  return new Promise((resolve) => {
    let idleTimer: ReturnType<typeof setTimeout>;
    const maxTimer = setTimeout(() => {
      observer.disconnect();
      clearTimeout(idleTimer);
      resolve();
    }, maxMs);

    const finish = () => {
      observer.disconnect();
      clearTimeout(maxTimer);
      resolve();
    };

    const observer = new MutationObserver(() => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(finish, idleMs);
    });

    observer.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    // Start the idle countdown immediately — if nothing mutates, we resolve
    idleTimer = setTimeout(finish, idleMs);
  });
}

async function renderAndCapture(
  container: HTMLDivElement,
  element: React.ReactElement,
  format: 'png' | 'jpeg'
): Promise<Blob> {
  const wrapper = document.createElement('div');
  wrapper.setAttribute('data-export-capture-root', 'true');
  wrapper.style.width = '1200px';
  wrapper.style.background = '#ffffff';
  container.appendChild(wrapper);

  const root = createRoot(wrapper);
  root.render(
    React.createElement(ThemeProvider, { theme }, element)
  );

  // Hide browser scrollbars in capture scope only.
  const style = document.createElement('style');
  style.textContent = `
    [data-export-capture-root] * {
      scrollbar-width: none !important;
    }
    [data-export-capture-root] *::-webkit-scrollbar {
      display: none !important;
    }
  `;
  wrapper.appendChild(style);

  // Hide the MeasureImpactsbyScope toolbar (controls Box containing
  // "Hide Chart Values", "Group by", fullscreen icon). We do this by hiding
  // the parent of a known control so the entire right-side cluster is removed.
  requestAnimationFrame(() => {
    const toolbarMarker = wrapper.querySelector('[data-testid="toggleLabelsButton"]');
    const toolbar = toolbarMarker?.parentElement as HTMLElement | null;
    if (toolbar) toolbar.style.display = 'none';
  });

  // Wait for React to commit and D3 to finish all transitions.
  // MutationObserver detects when the SVG DOM stops changing.
  await new Promise((r) => requestAnimationFrame(() => r(null)));
  await waitForStableDOM(wrapper, 400, 3000);

  const styleMutations: Array<{
    element: HTMLElement;
    width: string;
    height: string;
    overflow: string;
    overflowX: string;
    overflowY: string;
  }> = [];

  const mutateScrollableContainers = () => {
    const nodes = wrapper.querySelectorAll<HTMLElement>('*');
    nodes.forEach((node) => {
      const computed = window.getComputedStyle(node);
      const hasScrollOverflow =
        computed.overflowX === 'auto' ||
        computed.overflowX === 'scroll' ||
        computed.overflowY === 'auto' ||
        computed.overflowY === 'scroll';

      if (!hasScrollOverflow) {
        return;
      }

      styleMutations.push({
        element: node,
        width: node.style.width,
        height: node.style.height,
        overflow: node.style.overflow,
        overflowX: node.style.overflowX,
        overflowY: node.style.overflowY,
      });

      if (node.scrollWidth > node.clientWidth) {
        node.style.width = `${node.scrollWidth}px`;
      }
      if (node.scrollHeight > node.clientHeight) {
        node.style.height = `${node.scrollHeight}px`;
      }

      node.style.overflow = 'hidden';
      node.style.overflowX = 'hidden';
      node.style.overflowY = 'hidden';
    });

    wrapper.style.width = `${Math.max(wrapper.scrollWidth, 1200)}px`;
  };

  mutateScrollableContainers();

  try {
    const blob = await captureElementAsBlob(wrapper, format);
    return blob;
  } finally {
    styleMutations.forEach((mutation) => {
      mutation.element.style.width = mutation.width;
      mutation.element.style.height = mutation.height;
      mutation.element.style.overflow = mutation.overflow;
      mutation.element.style.overflowX = mutation.overflowX;
      mutation.element.style.overflowY = mutation.overflowY;
    });
    root.unmount();
    container.removeChild(wrapper);
  }
}

export async function exportCharts(
  params: ChartExportParams
): Promise<ChartExportOutcome> {
  const {
    config,
    scenarios,
    locationId,
    ghgScope,
    startYear,
    endYear,
    currency,
    container,
  } = params;

  const results: ChartExportResult[] = [];
  const errors: ChartExportError[] = [];
  const dateStr = new Date().toISOString().split('T')[0];
  const format = config.chartFormat;

  const selectedCharts = (Object.keys(config.charts) as ChartType[]).filter(
    (key) => config.charts[key]
  );

  if (selectedCharts.length === 0) return { results, errors };

  if (scenarios.length === 0) {
    for (const chartType of selectedCharts) {
      errors.push({
        chartType,
        scenario: '',
        reason: 'No decarbonization plan selected',
      });
    }
    return { results, errors };
  }

  for (const scenario of scenarios) {
    for (const chartType of selectedCharts) {
      try {
        const filterMetric = {
          locationId,
          dashboardSection: DASHBOARD_SECTIONS[chartType],
          ghgScope,
          scenario,
          startYear: startYear ?? null,
          endYear: endYear ?? null,
          businessUnit: null,
          minEmissionReduction: null,
          maxEmissionReduction: null,
          minMAC: null,
          maxMAC: null,
          groupBy: 'Type',
          scopes: [1, 2, 3],
        };

        const apiResponse = await _getMarginalAbatementCost(filterMetric);
        if (!apiResponse) {
          errors.push({ chartType, scenario, reason: 'No data returned from server' });
          continue;
        }

        const chartElement = createChartElement(
          chartType,
          apiResponse,
          currency,
          startYear,
          endYear
        );
        if (!chartElement) {
          errors.push({ chartType, scenario, reason: 'No data available to render' });
          continue;
        }

        const blob = await renderAndCapture(container, chartElement, format);
        if (!blob || blob.size === 0) {
          errors.push({ chartType, scenario, reason: 'Capture produced an empty image' });
          continue;
        }
        const safeName = scenario.replace(/\s+/g, '_');
        results.push({
          filename: `Charts/${CHART_FILE_NAMES[chartType]}_${safeName}_${dateStr}.${format}`,
          blob,
        });
      } catch (err) {
        const reason = err instanceof Error ? err.message : String(err);
        console.error(
          `Failed to export ${chartType} for scenario "${scenario}":`,
          err
        );
        errors.push({ chartType, scenario, reason });
      }
    }
  }

  return { results, errors };
}

export function describeChartExportError(err: ChartExportError): string {
  const label = CHART_LABELS[err.chartType] ?? err.chartType;
  return err.scenario
    ? `${label} (${err.scenario}): ${err.reason}`
    : `${label}: ${err.reason}`;
}
